// ─── LifeDiver Global Localisation Layer ─────────────────────────────────────
// Supports users globally — country determines context, not identity.

export interface LocaleConfig {
  country: string;
  countryCode: string;
  region?: string;
  city?: string;
  timezone: string;
  language: string;
  languageCode: string;
  currency: string;
  currencySymbol: string;
  currencyCode: string;
  dateFormat: string; // 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'
  timeFormat: '12h' | '24h';
  numberFormat: 'en-GB' | 'en-US' | 'de-DE' | 'fr-FR' | 'ja-JP' | 'pt-BR' | string;
  measurementUnit: 'metric' | 'imperial';
  weekStartsOn: 0 | 1 | 6; // 0=Sun, 1=Mon, 6=Sat
  rtl: boolean;
  workingDays: number[]; // 0=Sun ... 6=Sat
  publicHolidayRegion?: string;
}

export const LOCALE_PRESETS: Record<string, LocaleConfig> = {
  GB: {
    country: 'United Kingdom',
    countryCode: 'GB',
    timezone: 'Europe/London',
    language: 'English',
    languageCode: 'en-GB',
    currency: 'Pound Sterling',
    currencySymbol: '£',
    currencyCode: 'GBP',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    numberFormat: 'en-GB',
    measurementUnit: 'metric',
    weekStartsOn: 1,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  US: {
    country: 'United States',
    countryCode: 'US',
    timezone: 'America/New_York',
    language: 'English',
    languageCode: 'en-US',
    currency: 'US Dollar',
    currencySymbol: '$',
    currencyCode: 'USD',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    numberFormat: 'en-US',
    measurementUnit: 'imperial',
    weekStartsOn: 0,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  NG: {
    country: 'Nigeria',
    countryCode: 'NG',
    timezone: 'Africa/Lagos',
    language: 'English',
    languageCode: 'en-NG',
    currency: 'Nigerian Naira',
    currencySymbol: '₦',
    currencyCode: 'NGN',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    numberFormat: 'en-NG',
    measurementUnit: 'metric',
    weekStartsOn: 1,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  IN: {
    country: 'India',
    countryCode: 'IN',
    timezone: 'Asia/Kolkata',
    language: 'English',
    languageCode: 'en-IN',
    currency: 'Indian Rupee',
    currencySymbol: '₹',
    currencyCode: 'INR',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    numberFormat: 'en-IN',
    measurementUnit: 'metric',
    weekStartsOn: 1,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  JP: {
    country: 'Japan',
    countryCode: 'JP',
    timezone: 'Asia/Tokyo',
    language: 'Japanese',
    languageCode: 'ja-JP',
    currency: 'Japanese Yen',
    currencySymbol: '¥',
    currencyCode: 'JPY',
    dateFormat: 'YYYY/MM/DD',
    timeFormat: '24h',
    numberFormat: 'ja-JP',
    measurementUnit: 'metric',
    weekStartsOn: 0,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  BR: {
    country: 'Brazil',
    countryCode: 'BR',
    timezone: 'America/Sao_Paulo',
    language: 'Portuguese',
    languageCode: 'pt-BR',
    currency: 'Brazilian Real',
    currencySymbol: 'R$',
    currencyCode: 'BRL',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    numberFormat: 'pt-BR',
    measurementUnit: 'metric',
    weekStartsOn: 0,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  DE: {
    country: 'Germany',
    countryCode: 'DE',
    timezone: 'Europe/Berlin',
    language: 'German',
    languageCode: 'de-DE',
    currency: 'Euro',
    currencySymbol: '€',
    currencyCode: 'EUR',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    numberFormat: 'de-DE',
    measurementUnit: 'metric',
    weekStartsOn: 1,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  FR: {
    country: 'France',
    countryCode: 'FR',
    timezone: 'Europe/Paris',
    language: 'French',
    languageCode: 'fr-FR',
    currency: 'Euro',
    currencySymbol: '€',
    currencyCode: 'EUR',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    numberFormat: 'fr-FR',
    measurementUnit: 'metric',
    weekStartsOn: 1,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  SA: {
    country: 'Saudi Arabia',
    countryCode: 'SA',
    timezone: 'Asia/Riyadh',
    language: 'Arabic',
    languageCode: 'ar-SA',
    currency: 'Saudi Riyal',
    currencySymbol: '﷼',
    currencyCode: 'SAR',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    numberFormat: 'ar-SA',
    measurementUnit: 'metric',
    weekStartsOn: 0,
    rtl: true,
    workingDays: [0, 1, 2, 3, 4], // Sun–Thu
  },
  AU: {
    country: 'Australia',
    countryCode: 'AU',
    timezone: 'Australia/Sydney',
    language: 'English',
    languageCode: 'en-AU',
    currency: 'Australian Dollar',
    currencySymbol: 'A$',
    currencyCode: 'AUD',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    numberFormat: 'en-AU',
    measurementUnit: 'metric',
    weekStartsOn: 1,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  CA: {
    country: 'Canada',
    countryCode: 'CA',
    timezone: 'America/Toronto',
    language: 'English',
    languageCode: 'en-CA',
    currency: 'Canadian Dollar',
    currencySymbol: 'C$',
    currencyCode: 'CAD',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: '12h',
    numberFormat: 'en-CA',
    measurementUnit: 'metric',
    weekStartsOn: 0,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  ZA: {
    country: 'South Africa',
    countryCode: 'ZA',
    timezone: 'Africa/Johannesburg',
    language: 'English',
    languageCode: 'en-ZA',
    currency: 'South African Rand',
    currencySymbol: 'R',
    currencyCode: 'ZAR',
    dateFormat: 'YYYY/MM/DD',
    timeFormat: '24h',
    numberFormat: 'en-ZA',
    measurementUnit: 'metric',
    weekStartsOn: 0,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
  MX: {
    country: 'Mexico',
    countryCode: 'MX',
    timezone: 'America/Mexico_City',
    language: 'Spanish',
    languageCode: 'es-MX',
    currency: 'Mexican Peso',
    currencySymbol: 'MX$',
    currencyCode: 'MXN',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    numberFormat: 'es-MX',
    measurementUnit: 'metric',
    weekStartsOn: 1,
    rtl: false,
    workingDays: [1, 2, 3, 4, 5],
  },
};

export const ALL_COUNTRIES = Object.entries(LOCALE_PRESETS).map(([code, config]) => ({
  code,
  name: config.country,
  flag: getFlagEmoji(code),
  currency: config.currencySymbol,
  currencyCode: config.currencyCode,
}));

export function getFlagEmoji(countryCode: string): string {
  const flags: Record<string, string> = {
    GB: '🇬🇧', US: '🇺🇸', NG: '🇳🇬', IN: '🇮🇳', JP: '🇯🇵',
    BR: '🇧🇷', DE: '🇩🇪', FR: '🇫🇷', SA: '🇸🇦', AU: '🇦🇺',
    CA: '🇨🇦', ZA: '🇿🇦', MX: '🇲🇽',
  };
  return flags[countryCode] || '🌍';
}

export function getLocaleConfig(countryCode: string): LocaleConfig {
  return LOCALE_PRESETS[countryCode] || LOCALE_PRESETS['US'];
}

export function formatCurrency(amount: number, locale: LocaleConfig): string {
  return `${locale.currencySymbol}${amount.toLocaleString()}`;
}

export function formatDate(date: Date, locale: LocaleConfig): string {
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear();
  if (locale.dateFormat === 'MM/DD/YYYY') return `${m}/${d}/${y}`;
  if (locale.dateFormat === 'YYYY-MM-DD') return `${y}-${m}-${d}`;
  if (locale.dateFormat === 'YYYY/MM/DD') return `${y}/${m}/${d}`;
  if (locale.dateFormat === 'DD.MM.YYYY') return `${d}.${m}.${y}`;
  return `${d}/${m}/${y}`;
}

export function isWorkingDay(date: Date, locale: LocaleConfig): boolean {
  return locale.workingDays.includes(date.getDay());
}

export function addWorkingDays(date: Date, days: number, locale: LocaleConfig): Date {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (isWorkingDay(result, locale)) added++;
  }
  return result;
}

// Default locale — will be overridden by user preferences
// NOTE: No longer defaults to GB. Use detectBrowserLocale() or null for new users.
export const DEFAULT_LOCALE = LOCALE_PRESETS['US']; // Neutral fallback for display only

// ─── Locale persistence ───────────────────────────────────────────────────────

const LOCALE_STORAGE_KEY = 'lifediver_locale';

export function saveLocaleToStorage(countryCode: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(LOCALE_STORAGE_KEY, countryCode);
  }
}

export function loadLocaleFromStorage(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(LOCALE_STORAGE_KEY);
  }
  return null;
}

/**
 * Detect the user's locale from the browser's language settings.
 * Maps browser language codes to our supported country codes.
 * Returns null if no match found — caller should prompt user to select.
 */
export function detectBrowserLocale(): string | null {
  if (typeof window === 'undefined') return null;
  const lang = navigator.language || '';
  const langMap: Record<string, string> = {
    'en-GB': 'GB',
    'en-US': 'US',
    'en-AU': 'AU',
    'en-CA': 'CA',
    'en-NG': 'NG',
    'en-ZA': 'ZA',
    'en-IN': 'IN',
    'hi': 'IN',
    'hi-IN': 'IN',
    'ja': 'JP',
    'ja-JP': 'JP',
    'pt-BR': 'BR',
    'pt': 'BR',
    'de': 'DE',
    'de-DE': 'DE',
    'fr': 'FR',
    'fr-FR': 'FR',
    'ar': 'SA',
    'ar-SA': 'SA',
    'es-MX': 'MX',
    'es': 'MX',
  };
  // Try exact match first, then language prefix
  if (langMap[lang]) return langMap[lang];
  const prefix = lang.split('-')[0];
  if (langMap[prefix]) return langMap[prefix];
  // Try to match by country suffix (e.g. en-AU → AU)
  const suffix = lang.split('-')[1];
  if (suffix && LOCALE_PRESETS[suffix]) return suffix;
  return null;
}

/**
 * Get the active locale: stored preference → browser detection → null (not assumed).
 * Returns null when no locale has been configured — UI should prompt user to select.
 */
export function getActiveLocale(): LocaleConfig | null {
  const stored = loadLocaleFromStorage();
  if (stored && LOCALE_PRESETS[stored]) return LOCALE_PRESETS[stored];
  const detected = detectBrowserLocale();
  if (detected && LOCALE_PRESETS[detected]) return LOCALE_PRESETS[detected];
  return null;
}

// ─── Currency formatting ──────────────────────────────────────────────────────

/**
 * Exchange rates relative to GBP (approximate, for demo personalisation).
 * In production these would come from a live FX API.
 */
export const APPROXIMATE_RATES_FROM_GBP: Record<string, number> = {
  GBP: 1,
  USD: 1.27,
  EUR: 1.17,
  NGN: 2050,
  INR: 106,
  JPY: 196,
  BRL: 6.4,
  SAR: 4.76,
  AUD: 1.96,
  CAD: 1.73,
  ZAR: 23.5,
  MXN: 21.8,
};

/**
 * Convert a GBP amount to the target locale's currency.
 */
export function convertFromGBP(gbpAmount: number, targetCurrencyCode: string): number {
  const rate = APPROXIMATE_RATES_FROM_GBP[targetCurrencyCode] ?? 1;
  return gbpAmount * rate;
}

/**
 * Format a numeric amount as a currency string for the given locale.
 * Uses the locale's number format and currency symbol.
 */
export function formatAmount(amount: number, locale: LocaleConfig, options?: { decimals?: boolean }): string {
  const showDecimals = options?.decimals !== false;
  // For JPY and NGN, no decimal places by convention
  const noDecimals = ['JPY', 'NGN'].includes(locale.currencyCode);
  const rounded = noDecimals ? Math.round(amount) : Math.round(amount * 100) / 100;

  let formatted: string;
  try {
    formatted = rounded.toLocaleString(locale.numberFormat, {
      minimumFractionDigits: noDecimals || !showDecimals ? 0 : 2,
      maximumFractionDigits: noDecimals || !showDecimals ? 0 : 2,
    });
  } catch {
    formatted = rounded.toLocaleString();
  }

  // Symbol placement: most currencies prefix, some suffix (e.g. some European)
  const suffixCurrencies = ['EUR']; // de-DE and fr-FR place € after the number
  if (suffixCurrencies.includes(locale.currencyCode) && ['de-DE', 'fr-FR'].includes(locale.numberFormat)) {
    return `${formatted} ${locale.currencySymbol}`;
  }
  return `${locale.currencySymbol}${formatted}`;
}

/**
 * Convert a GBP amount and format it for the target locale.
 */
export function localiseAmount(gbpAmount: number, locale: LocaleConfig, options?: { decimals?: boolean }): string {
  const converted = convertFromGBP(gbpAmount, locale.currencyCode);
  return formatAmount(converted, locale, options);
}

/**
 * Replace a GBP-formatted string like "£84.99" or "£2,840" with the locale equivalent. * Also handles patterns like"£87/month", "£10.99 → £12.99", "+£24/year".
 */
export function localiseMoneyString(text: string, locale: LocaleConfig): string {
  if (locale.currencyCode === 'GBP') return text; // No conversion needed
  // Match £ followed by optional + or -, digits, optional comma-separated groups, optional decimal
  return text.replace(/£([\d,]+(?:\.\d{1,2})?)/g, (_, numStr) => {
    const amount = parseFloat(numStr.replace(/,/g, ''));
    if (isNaN(amount)) return _;
    return localiseAmount(amount, locale);
  });
}
